import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { useMst } from '../model/Root';
import { Button, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import FormFieldSwitch from '../components/FormComponents/FormFieldSwitch';
import FormFieldDropDown from '../components/FormComponents/FormFieldDropDown';
import FormFieldNumber from '../components/FormComponents/FormFieldNumber';
import FormFieldText from '../components/FormComponents/FormFieldText';
import { KeyValue } from '../types';
import {
  getChurchDiscountPlatformMappings,
  getChurchDiscountSettings,
  updateChurchDiscountPlatformMappings,
  updateChurchDiscountSettings,
} from '../services/churchApi';

const typeOptions: KeyValue[] = [
  { key: 'percentage', text: 'percentage' },
  { key: 'fixed', text: 'fixed' },
];

const Schema = Yup.object().shape({
  enabled: Yup.boolean().required(),
  type: Yup.mixed<'percentage' | 'fixed'>().oneOf(['percentage', 'fixed']).required(),
  value: Yup.number()
    .when('type', {
      is: 'percentage',
      then: (schema) => schema.min(0).max(100).required(),
    })
    .when('type', {
      is: 'fixed',
      then: (schema) => schema.min(0).required(),
    }),
});

const SettingsChurchDiscount = observer(() => {
  const { t } = useTranslation();
  const { errorStore } = useMst();
  const { setError, setSuccess } = errorStore;

  const [initialValues, setInitialValues] = useState<{
    enabled: boolean;
    type: 'percentage' | 'fixed';
    value: number;
    stripeCouponId?: string;
    googleProductMap?: string;
    appleProductMap?: string;
  }>({ enabled: false, type: 'percentage', value: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [settings, platforms] = await Promise.all([
          getChurchDiscountSettings(),
          getChurchDiscountPlatformMappings(),
        ]);
        setInitialValues({
          enabled: settings?.enabled ?? false,
          type: settings?.type ?? 'percentage',
          value: settings?.value ?? 0,
          stripeCouponId: platforms?.stripeCouponId ?? undefined,
          googleProductMap: platforms?.googleProductMap ? JSON.stringify(platforms.googleProductMap) : undefined,
          appleProductMap: platforms?.appleProductMap ? JSON.stringify(platforms.appleProductMap) : undefined,
        });
      } catch (e) {
        // ignore - page will still render, user can try saving
      }
    })();
  }, []);

  return (
    <Layout title={t('settings.discounts.church')}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Schema}
        onSubmit={async (values, helpers) => {
          const { enabled, type, value } = values;
          try {
            await updateChurchDiscountSettings({ enabled, type, value });
            setSuccess('save_success');
          } catch (e: any) {
            setError('save_error');
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, isValid, values }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormFieldSwitch name="enabled" label={t('enabled') ?? 'Enabled'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldDropDown name="type" label={t('type') ?? 'Type'} values={typeOptions} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldNumber name="value" label={t('value') ?? 'Value'} />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <h3>{t('platform_mappings') ?? 'Platform mappings'}</h3>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="stripeCouponId" label={'Stripe coupon ID'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="googleProductMap" label={'Google Product Map JSON'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="appleProductMap" label={'Apple Product Map JSON'} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={async () => {
                    const stripeCouponId = values?.stripeCouponId;
                    const googleProductMap = values?.googleProductMap;
                    const appleProductMap = values?.appleProductMap;
                    try {
                      await updateChurchDiscountPlatformMappings({
                        stripeCouponId: stripeCouponId || undefined,
                        googleProductMap: googleProductMap ? JSON.parse(googleProductMap) : undefined,
                        appleProductMap: appleProductMap ? JSON.parse(appleProductMap) : undefined,
                      });
                      setSuccess('save_success');
                      // Refresh to reflect normalized values from backend
                      const platforms = await getChurchDiscountPlatformMappings();
                      setInitialValues((prev) => ({
                        ...prev,
                        stripeCouponId: platforms?.stripeCouponId ?? undefined,
                        googleProductMap: platforms?.googleProductMap
                          ? JSON.stringify(platforms.googleProductMap)
                          : undefined,
                        appleProductMap: platforms?.appleProductMap
                          ? JSON.stringify(platforms.appleProductMap)
                          : undefined,
                      }));
                    } catch (e) {
                      setError('save_error');
                    }
                  }}
                >
                  {t('save_platform_mappings') ?? 'Save platform mappings'}
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2} display="flex" gap={1}>
              <Button type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                {t('save') ?? 'Save'}
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </Layout>
  );
});

export default SettingsChurchDiscount;
